import { type FC } from "react";

import {
  Pagination,
  PaginationContent,
  PaginationItem,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";

import { BaseSelect, type ItemsListType } from "../BaseSelect";
import { PaginationNext, PaginationPrevious } from "../CustomPagination";
import { SimpleText } from "../SimpleText";

const rowsListDefault: ItemsListType = [
  { name: "25", id: "25" },
  { name: "50", id: "50" },
  { name: "100", id: "100" },
];

interface TablePaginationProps {
  classes?: string;
  next?: () => void;
  prev?: () => void;
  limit?: number;
  offset?: number;
  total: number;
  setLimit: (value: number) => void;
}

const TablePagination: FC<TablePaginationProps> = ({
  classes,
  next,
  prev,
  limit = 25,
  offset = 0,
  total,
  setLimit,
}) => {
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);

  const handlePrev = () => {
    if (offset - limit >= 0) {
      prev?.();
    }
  };

  const handleNext = () => {
    if (offset + limit < total) {
      next?.();
    }
  };

  const currentCount = Math.min(offset + limit, total);

  return (
    <div
      className={cn(
        "flex items:start md:items-center flex-col md:flex-row gap-2 md:gap-0 justify-between px-0 md:px-[8px] mt-4",
        classes
      )}
    >
      <SimpleText>
        {currentCount} of {total} row(s) selected.
      </SimpleText>
      <div className="flex items-center justify-between md:justify-end">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-[7px] mr-8">
          <span className="text-sm">Rows per page</span>

          <BaseSelect
            value={String(limit)}
            onChange={val => setLimit(Number(val))}
            itemsList={rowsListDefault}
            className="w-[70px] h-[32px]"
            triggerProps={{
              "data-testid": "table-pagination-select",
            }}
          />
        </div>
        <span className="text-sm mr-0 md:mr-10 self-end md:self-auto mb-1.5 md:mb-0">
          Page {currentPage} of {totalPages}
        </span>
        <Pagination
          className="text-gray-950 justify-end mx-0 w-fit self-end md:self-auto"
          data-testid="table-pagination"
        >
          <PaginationContent className="gap-2">
            <PaginationItem data-testid="table-pagination-prev">
              <PaginationPrevious
                className={cn(
                  "text-sm p-[7px] cursor-pointer",
                  currentPage === 1 &&
                    "opacity-50 cursor-auto hover:bg-transparent"
                )}
                size="md"
                onClick={handlePrev}
              />
            </PaginationItem>
            <PaginationItem data-testid="table-pagination-next">
              <PaginationNext
                className={cn(
                  "text-sm p-[7px] cursor-pointer",
                  currentPage === totalPages &&
                    "opacity-50 cursor-auto hover:bg-transparent"
                )}
                size="md"
                onClick={handleNext}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  );
};

export default TablePagination;
